// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="AppTopbar" set="mytaible-shadcn-studio" verified=tbd

import { ArrowLeft, Search } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '../atoms/avatar';
import { Button } from '../atoms/button';
import { Separator } from '../atoms/separator';
import { Input } from '../forms/primitives/input';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AppTopbarUser {
  name: string;
  role?: string;
  avatarSrc?: string;
  avatarFallback?: string;
}

export type AppTopbarVariant = 'search' | 'page-header';

export interface AppTopbarProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Layout variant.
   * - `search` (default): search input left, actions + avatar right
   * - `page-header`: breadcrumb + title left, actions right (dashboard-header-02 style)
   */
  variant?: AppTopbarVariant;
  // ── search variant ──────────────────────────────────────────────────────────
  /** Show a back button on the left (both variants) */
  showBack?: boolean;
  onBack?: () => void;
  /** Show the search input (search variant only) */
  showSearch?: boolean;
  searchPlaceholder?: string;
  onSearch?: (value: string) => void;

  // ── page-header variant ─────────────────────────────────────────────────────
  /** Breadcrumb trail rendered left of the page title (page-header variant) */
  breadcrumb?: React.ReactNode;
  /** Primary page title (page-header variant) */
  pageTitle?: string;

  // ── shared slots ────────────────────────────────────────────────────────────
  /** Right-side action buttons slot */
  actions?: React.ReactNode;
  /** Current user — avatar on the far right */
  user?: AppTopbarUser;
  /** Sidebar toggle injected by AppShell for mobile */
  sidebarToggle?: React.ReactNode;
}

// ─── AppTopbar ────────────────────────────────────────────────────────────────

const AppTopbar = React.forwardRef<HTMLElement, AppTopbarProps>(
  (
    {
      className,
      variant = 'search',
      showBack = false,
      onBack,
      showSearch = true,
      searchPlaceholder = 'Type to search…',
      onSearch,
      breadcrumb,
      pageTitle,
      actions,
      user,
      sidebarToggle,
      ...props
    },
    ref,
  ) => {
    const initials = user?.name
      ? user.name
          .split(' ')
          .map((n) => n[0])
          .join('')
          .toUpperCase()
          .slice(0, 2)
      : '?';

    return (
      <header
        ref={ref}
        className={cn(
          'flex h-14 shrink-0 items-center justify-between border-b border-border bg-[var(--color-topbar,var(--color-background))] px-4 gap-3',
          className,
        )}
        {...props}
      >
        {/* Left side */}
        <div className="flex items-center gap-2 min-w-0">
          {sidebarToggle}

          {showBack && (
            <>
              <Button
                appearance="ghost"
                size="icon"
                aria-label="Go back"
                onClick={onBack}
                className="shrink-0 text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground"
              >
                <ArrowLeft className="size-5" />
              </Button>
              <Separator orientation="vertical" className="h-5 shrink-0" />
            </>
          )}

          {variant === 'search' && showSearch && (
            <div className="relative flex items-center">
              <Search
                className="pointer-events-none absolute left-3 size-4 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                type="search"
                placeholder={searchPlaceholder}
                className="h-9 w-64 pl-9 text-sm"
                onChange={(e) => onSearch?.(e.target.value)}
              />
            </div>
          )}

          {variant === 'page-header' && (
            <div className="flex flex-col justify-center min-w-0">
              {breadcrumb && (
                <div className="text-xs text-muted-foreground leading-none mb-0.5">
                  {breadcrumb}
                </div>
              )}
              {pageTitle && (
                <h1 className="text-sm font-semibold text-foreground truncate leading-tight">
                  {pageTitle}
                </h1>
              )}
            </div>
          )}
        </div>

        {/* Right — actions + avatar */}
        <div className="flex shrink-0 items-center gap-1">
          {actions}

          {user && (
            <Avatar size="sm" shape="rounded" className="ml-1 cursor-pointer">
              {user.avatarSrc && <AvatarImage src={user.avatarSrc} alt={user.name} />}
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
          )}
        </div>
      </header>
    );
  },
);
AppTopbar.displayName = 'AppTopbar';

export { AppTopbar };
